import toast from 'react-hot-toast'
import {
  type GapAnalysisViewDto,
  useCreateGapAnalysisViewMutation,
  useDeleteGapAnalysisMutation,
  useUpdateGapAnalysisViewMutation
} from '../../../../API/rtk-query-api/store/tmsApi'
import useFormattedFilters from '../../../../mixins/useGAFilters'
import { useGAViewsManagement } from '../../../../mixins/useGAViewsManagement'
import { useGapAnalysisViews } from '../../../../mixins/useGapAnalysisViews'
import { gapAnalysisActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import SaveViewModal from '../../../UI/view-management/SaveViewModal'
import ViewManagementModal from '../../../UI/view-management/view-management-modal'

const GapAnalysisViewManagementModal = () => {
  const filters = useFormattedFilters()
  const viewManagement = useGAViewsManagement()

  const dispatch = useAppDispatch()
  const gapAnalysisFiltersState = useAppSelector(state => state.gapAnalysisFilters)
  const gapAnalysisState = useAppSelector(state => state.gapAnalysis)

  const { views: gapAnalysisViews } = useGapAnalysisViews()

  const [saveView, { isLoading: isSavingView }] = useCreateGapAnalysisViewMutation()
  const [updateView] = useUpdateGapAnalysisViewMutation()
  const [deleteView] = useDeleteGapAnalysisMutation()

  const isDuplicatedViewName = (title: string | undefined, id?: string | undefined) => {
    return gapAnalysisViews.filter(view => view.id !== id).some(view => view.title === title)
  }

  const onSaveView = (name: string, isGlobal: boolean) => {
    if (name === '') {
      toast.error('Name is required')
    } else if (isDuplicatedViewName(name)) {
      toast.error('A view with that name already exists')
    } else {
      saveView({
        gapAnalysisViewDto: {
          title: name,
          global: isGlobal,
          filters: {
            ...filters
          },
          settings: {
            gridlineFrequency: gapAnalysisFiltersState.gridlinesFrequency,
            showGridlines: gapAnalysisFiltersState.showGridSeparator,
            isManagerColumnSelected: gapAnalysisFiltersState.showManagersColumn,
            selectedColumns: gapAnalysisFiltersState.selectedColumns.map(column => column.value)
          }
        }
      })
        .unwrap()
        .then(
          response => {
            toast.success('View has been saved')
            dispatch(
              gapAnalysisActions.setSelectedView({
                value: response.id ?? '',
                text: response.title ?? ''
              })
            )
            dispatch(gapAnalysisActions.setShowSaveViewModal(false))
          },
          response => {
            const errors = response?.data?.validationStatus?.errors
            if (errors && Array.isArray(errors)) {
              if (errors[0] === 'A view with this name already exists in this scope!') {
                toast.error('A view with that name already exists')
              }
            } else {
              toast.error('Save failed')
            }
          }
        )
    }
  }

  const onUpdateView = (updatedView: GapAnalysisViewDto) => {
    if (updatedView.id) {
      updateView({
        id: updatedView.id,
        gapAnalysisViewDto: {
          title: updatedView.title,
          global: updatedView.global,
          settings: {
            gridlineFrequency: gapAnalysisFiltersState.gridlinesFrequency,
            showGridlines: gapAnalysisFiltersState.showGridSeparator,
            isManagerColumnSelected: gapAnalysisFiltersState.showManagersColumn,
            selectedColumns: gapAnalysisFiltersState.selectedColumns.map(column => column.value)
          },
          filters: {
            ...filters
          }
        }
      })
        .unwrap()
        .then(response => {
          toast.success('View has been updated')
          if (gapAnalysisState.selectedView?.value === response.id) {
            dispatch(
              gapAnalysisActions.setSelectedView({
                value: response.id ?? '',
                text: response.title ?? ''
              })
            )
            dispatch(gapAnalysisActions.setShowSaveViewModal(false))
          }
        })
    }
  }

  const onRenameView = (updatedView: GapAnalysisViewDto) => {
    if (!isDuplicatedViewName(updatedView.title, updatedView.id) && updatedView.id) {
      updateView({
        id: updatedView.id,
        gapAnalysisViewDto: { ...updatedView }
      })
        .unwrap()
        .then(response => {
          toast.success('View has been updated')
          if (gapAnalysisState.selectedView?.value === response.id) {
            dispatch(
              gapAnalysisActions.setSelectedView({
                value: response.id ?? '',
                text: response.title ?? ''
              })
            )
          }
        })
    } else {
      toast.error('A view with that name already exists')
    }
  }

  const onRemoveView = (view: GapAnalysisViewDto) => {
    if (view.id) {
      deleteView({ id: view.id })
        .unwrap()
        .then(
          () => {
            toast.success('View has been deleted')
            if (gapAnalysisState.selectedView?.value === view.id) {
              viewManagement.handleRemoveSelectedGAView()
            }
          },
          () => {
            toast.error('Error removing view')
          }
        )
    }
  }

  return (
    <>
      <SaveViewModal
        isModalVisible={gapAnalysisState.showSaveViewModal}
        viewsToManage={gapAnalysisViews}
        isLoading={isSavingView}
        onSaveView={onSaveView}
        onUpdateView={onUpdateView}
        onHideModal={() => dispatch(gapAnalysisActions.setShowSaveViewModal(false))}
        selectedViewId={gapAnalysisState.selectedView?.value}
      />
      <ViewManagementModal
        isModalVisible={gapAnalysisState.showManageViewModal}
        viewsToManage={gapAnalysisViews}
        onRemoveView={onRemoveView}
        onRenameView={onRenameView}
        onHideModal={() => dispatch(gapAnalysisActions.setShowManageViewModal(false))}
      />
    </>
  )
}

export default GapAnalysisViewManagementModal
